import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {AuthInterceptor as OidcAuthInterceptor} from 'angular-auth-oidc-client';
import {NGXLogger} from 'ngx-logger';
import {Observable} from 'rxjs';
import {PortalMessageBroker} from '../portal/portal-message-broker';
import {AdnovaTokenService} from '../portal/adnova-token.service';
import {Config, ConfigService} from '../config/service/config.service';


@Injectable()
export class AuthInterceptor
  implements HttpInterceptor {

  private readonly _destroyRef = inject(DestroyRef);

  private config?: Config;

  constructor(
    private readonly logger: NGXLogger,
    private readonly configService: ConfigService,
    private readonly portalMessageBroker: PortalMessageBroker,
    private readonly adnovaTokenService: AdnovaTokenService,
    private readonly oidcAuthInterceptor: OidcAuthInterceptor,
  ) {
    this.configService.loaded$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(value => {
        this.config = value;
      });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {

    // INFO: check for intercepting
    const matchesSecureRoots = this.matchesSecureRoots(request.url);
    if (!matchesSecureRoots) {
      return next.handle(request);
    }

    if (this.portalMessageBroker.isRunningInPortal()) {
      // INFO: intercepting running in portal mode
      const token = this.adnovaTokenService.getToken();
      if (!token) {
        this.logger.debug(`Wanted to add token to ${request.url} but found no token: '${token}'`);
        return next.handle(request);
      }

      this.logger.trace(`Using AuthInterceptor in portal mode`);
      request = request.clone({
        headers: request.headers.set('Authorization', 'Bearer ' + token),
      });
      return next.handle(request);
    }

    // INFO: intercepting running in standalone mode
    this.logger.trace(`Using AuthInterceptor in standalone mode`);
    return this.oidcAuthInterceptor.intercept(request, next);
  }

  private matchesSecureRoots(
    route: string,
  ): boolean {
    const exportConfig = this.config?.exportConfig;
    if (!exportConfig) {
      return false;
    }

    const secureRoutes = [
      exportConfig.apiBaseUrl,
    ];
    for (const configuredRoute of secureRoutes) {
      if (route.startsWith(configuredRoute)) {
        this.logger.trace(`'${route}' matches configured route '${configuredRoute}'`);
        return true;
      }
    }
    this.logger.trace(`Did not find any configured route for route ${route}`);
    return false;
  }
}
